import { Inject, Injectable } from '@angular/core';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { EXPANDS } from '../../enums/expands.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { UserModel } from '../../models/complex/user.model';
import { EnvironmentService } from '@evorait/pace-environment';

@Injectable()
export class UriUtilitiesService {
    private readonly endpointsWithSalesOrgAndPlantFilter: string[] = [
        ENDPOINTS.ConfigSet,
        ENDPOINTS.CseDopSet,
        ENDPOINTS.InputFieldProperties,
        ENDPOINTS.ValueHelpers,
        ENDPOINTS.NotificationHeaders,
        ENDPOINTS.WhatsNew,
        ENDPOINTS.WorkScheduleSet,
        ENDPOINTS.MaterialStockSet,
    ];

    private readonly endpointsWithFrontId: string[] = [
        ENDPOINTS.WorkScheduleSet,
        ENDPOINTS.MaterialStockSet,
        ENDPOINTS.MeasToolSet,
        ENDPOINTS.PhysicalInventorySet,
        ENDPOINTS.PIScheduleSet,
    ];
    public constructor(
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly environmentService: EnvironmentService,
    ) {}

    public getUriForContent(): string {
        if (this.environmentService.msalEnabled || this.environmentService.auth0Enabled) {
            return `${this.environmentService.apiBase}/${this.environmentService.apiSapProxy}`;
        }

        return `${this.environmentService.apiSapProxy}`;
    }

    /**
     * Appends parameters to a URL.
     *
     * @param url
     * @param parameter
     */
    public appendParameter(url: string, parameter: string): string {
        const separator = url.indexOf('?') < 0 ? '?' : '&';
        return `${url}${separator}${parameter}`;
    }

    /**
     * Appends filters to defining requests urls
     *
     * @param definingRequests
     * @param selectedUserModel
     */
    public appendFiltersForDefiningRequests(definingRequests: string[], selectedUserModel: UserModel): string[] {
        return definingRequests.map((definingRequest) => this.appendUriFilters(definingRequest, selectedUserModel));
    }

    /**
     * Appends filters to a URL.
     *
     * @param url
     * @param selectedUserModel
     */
    public appendUriFilters(url: string, selectedUserModel: UserModel): string {
        const filterFrontId = `FrontId eq '${selectedUserModel.getFrontId()}'`;
        if (this.endpointsWithFrontId.find((ep) => url.indexOf(ep) > -1)) {
            url = this.appendFilterValue(url, filterFrontId);
        }
        return url;
    }

    /**
     * Appends an OData filter value to a URL
     *
     * @param url
     * @param filterValue
     */
    public appendFilterValue(url: string, filterValue: string): string {
        const groups: { [keys: string]: string } = {};

        // split given URL in base URL and already existing parameters
        const separatorIndex = url.indexOf('?');
        let baseUrl = separatorIndex < 0 ? url : url.substr(0, separatorIndex);
        const urlParameters = separatorIndex >= 0 ? url.substr(separatorIndex + 1) : '';

        // if the URL already contains parameters, we split the string into individual parameters and store them for later
        if (urlParameters.length > 0) {
            const parameters = urlParameters.split('&');
            parameters.forEach((p) => {
                // extract key and value from each parameter
                const parameterAndValue = p.split('=');
                groups[parameterAndValue[0]] = parameterAndValue[1];
            });
        }

        // make sure filter entry exists
        groups.$filter = groups.$filter || '';

        // now add the new filter value
        groups.$filter = groups.$filter.length === 0 ? filterValue : `${groups.$filter} and ${filterValue}`;

        // reconstruct the entire URL

        const groupsKeys = Object.keys(groups);
        if (groupsKeys.length > 0) {
            let separator = '?';

            for (const p of groupsKeys) {
                baseUrl += `${separator}${p}=${groups[p]}`;
                separator = '&';
            }
        }

        return baseUrl;
    }

    /**
     * This function returns the notificationHeaderQuery based on the environment.
     */
    public getNotificationHeaderByAppId(): string {
        let notificationHeader = '';

        if (this.clientManager.isNgmP58()) {
            notificationHeader = EXPANDS.NotificationHeader;
        }

        return notificationHeader;
    }

    /**
     * This function returns CSML url by Appending multiple Material Order number and also single order
     * TODO: move me to a proper place!
     *
     * @param materials
     * @param csmlUrl
     */
    public getCSMLTrackerURL(materials: Array<{ orderNumber: { value: string } }>, csmlUrl: string): string {
        if (!csmlUrl) {
            return '';
        }
        return materials.reduce(
            (acc, material, currentIndex) =>
                `${acc}purchaseOrder[]=${material.orderNumber.value}${currentIndex < materials.length - 1 ? '&' : ''}`,
            csmlUrl,
        );
    }
}
